import type { FloorFile } from '../../content/floors/floorSchema';
import type { DungeonFloor, DungeonRoom, RoomStatus } from '../game/types';
import { discoverAdjacent } from './discover';

function roomKey(x: number, y: number): string {
  return `${x},${y}`;
}

/**
 * Build runtime floor from validated content.
 * Root is EXPLORED; its neighbors become DISCOVERED.
 */
export function loadFloorFromContent(data: FloorFile): DungeonFloor {
  const rootId = roomKey(data.root.x, data.root.y);
  const rooms: Record<string, DungeonRoom> = {};

  for (const raw of data.rooms) {
    const id = raw.id || roomKey(raw.gridPos.x, raw.gridPos.y);
    let status: RoomStatus = raw.status;

    if (id === rootId) {
      status = 'EXPLORED';
    }

    rooms[id] = {
      id,
      gridPos: { ...raw.gridPos },
      type: raw.type,
      status,
      name: raw.name,
      description: raw.description,
      safetyTag: raw.safetyTag,
      bgImageKey: raw.bgImageKey,
      thumbnailKey: raw.thumbnailKey,
      connections: raw.connections.map((c) => ({ ...c })),
      isCleared: raw.isCleared,
      isRequiredInteraction: raw.isRequiredInteraction,
      npcId: raw.npcId,
      options: raw.options.map((o) => ({
        ...o,
        consequence: { ...o.consequence },
      })),
    };
  }

  if (!rooms[rootId]) {
    throw new Error(`Floor root room "${rootId}" is missing from content.`);
  }

  let floor: DungeonFloor = {
    floorNumber: data.floorNumber,
    floorName: data.floorName,
    gridWidth: data.gridWidth,
    gridHeight: data.gridHeight,
    root: { ...data.root },
    rooms,
    playerRoomId: rootId,
    objectiveText: data.objectiveText,
    safeRoomId: data.safeRoomId,
    bossRoomId: data.bossRoomId,
    teleporterRoomId: data.teleporterRoomId,
  };

  floor = discoverAdjacent(floor, rootId);
  return floor;
}
